import { ROAD_SPIN_AREA as SPIN_AREA } from '@goodtime/shared';

/** How much of the Royal Road panel fits above the reels. */
export type PanelMode = 'full' | 'compact' | 'chip';

/** Panel heights by mode, in CSS pixels. */
export const PANEL_HEIGHT: Record<PanelMode, number> = { full: 180, compact: 116, chip: 44 };

/** The framed reels' minimum height before the panel gets any room: the
 *  reels and SPIN always come first. */
export const MIN_REELS = 240;

/** The widest the panel gets. */
const MAX_WIDTH = 640;
/** What the top bar's corners take on each side: the back button on the
 *  left, the heart and the coin bar on the right. */
export const TOP_BAR_SIDE = 260;
/** The narrowest panel worth putting beside the top bar. */
const MIN_BESIDE_WIDTH = 500;
/** Room kept under the SPIN row for "Play with others". */
export const BOTTOM_RESERVE = 76;

const GAP = 8;

/** Room between the panel and the reels for the slot's own chips (free
 *  spins left, hold-and-spin respins), which sit just above the frame. */
export const CHIP_ROOM = 40;

export interface ChromeLayout {
  mode: PanelMode;
  /** The panel sits between the top bar's corners (wide screens), so it
   *  can't grow past {@link panel}'s width. */
  beside: boolean;
  /** What the slot keeps clear above and below the reels. */
  insets: { top: number; bottom: number };
  /** Where the panel sits. */
  panel: { top: number; left: number; width: number; height: number };
}

/**
 * Where the Royal Road panel goes, and how big. Wide screens put it top
 * centre between the corner buttons; anything narrower puts it under the top
 * bar, as wide as the screen allows. The
 * biggest mode that leaves the reels at least {@link MIN_REELS} wins: full,
 * then compact, then a chip that opens a sheet.
 */
export function slotChromeLayout(vw: number, vh: number, safeTop = 0): ChromeLayout {
  // Beside the top bar only when the panel fits between the back button and
  // the coin bar without touching either; otherwise under the bar.
  const beside = vw - 2 * TOP_BAR_SIDE >= MIN_BESIDE_WIDTH;
  const width = beside ? Math.min(MAX_WIDTH, vw - 2 * TOP_BAR_SIDE) : Math.min(MAX_WIDTH, vw - 24);
  const left = Math.round((vw - width) / 2);
  // The title plaque rides the panel's top edge, so leave it room above.
  const top = Math.round(beside ? safeTop + 18 : safeTop + 70);
  const spare = vh - top - BOTTOM_RESERVE - SPIN_AREA - MIN_REELS - CHIP_ROOM;
  const mode: PanelMode =
    spare >= PANEL_HEIGHT.full + GAP ? 'full' : spare >= PANEL_HEIGHT.compact + GAP ? 'compact' : 'chip';
  const height = PANEL_HEIGHT[mode];
  return {
    mode,
    beside,
    insets: { top: top + height + CHIP_ROOM, bottom: BOTTOM_RESERVE },
    panel: { top, left, width, height },
  };
}
